import { Component, Input } from "@angular/core";
import { SubtaskModel } from "../../service/task-mgmt.service";

@Component({
  selector: "app-task-progress-indicator",
  templateUrl: "./task-progress-indicator.component.html",
  styleUrls: ["./task-progress-indicator.component.scss"],
})
export class TaskProgressIndicatorComponent {
  @Input() subtasks: SubtaskModel[] = [];
  @Input() showBar = true;
  @Input() compact = false;

  /** Lightweight alternative inputs for callers that only know the totals (e.g.
   *  the kanban card, which reads from the in-memory all-subtasks store). When
   *  the `subtasks` array is empty but `totalCount > 0`, these are used instead. */
  @Input() totalCount: number = 0;
  @Input() doneCount: number = 0;

  /** Done count derived from `subtasks` if present, otherwise from `doneCount`.
   *  Tolerates three status formats so legacy callers using the [subtasks] input
   *  keep working even after the ID-mapping refactor:
   *    1. legacy kanban-key string ('done' / 'cancelled')
   *    2. numeric API id stored as string (e.g. '2002') — looked up via `_raw.task_status_name`
   *    3. raw API status_name string ('Done' / 'Cancelled' / 'Closed' / 'Completed') */
  get completedCount(): number {
    if (this.subtasks && this.subtasks.length) {
      return this.subtasks.filter((st) => this.isDoneOrCancelled(st)).length;
    }
    return this.doneCount || 0;
  }

  private isDoneOrCancelled(st: any): boolean {
    if (!st) return false;
    // Prefer the API-supplied status name (preserved on _raw by the mapper).
    const rawName = (st?._raw?.task_status_name || '').toString().toLowerCase().trim();
    if (rawName) {
      return rawName === 'done' || rawName === 'closed'
        || rawName === 'complete' || rawName === 'completed'
        || rawName === 'cancelled' || rawName === 'canceled';
    }
    // Fall back to the legacy kanban-key string compare.
    const s = (st?.status || '').toString().toLowerCase().trim();
    return s === 'done' || s === 'cancelled';
  }

  /** Total count derived from `subtasks` if present, otherwise from `totalCount`. */
  get total(): number {
    if (this.subtasks && this.subtasks.length) {
      return this.subtasks.length;
    }
    return this.totalCount || 0;
  }

  get progressPercent(): number {
    const t = this.total;
    if (!t) return 0;
    return Math.round((this.completedCount / t) * 100);
  }
}
